import { useState, useEffect, useContext, useRef } from "react";
import styled from "styled-components";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Question from "./question";
import Button from "../../../../../components/button";
import EditIcon from "@material-ui/icons/Edit";
import { v4 as uuidv4 } from "uuid";
import {
  updatePortalSurvey,
  createPortalSurvey,
} from "../../../../../api";
import { DashboardContext } from "../../../dashboard-context";
import { AuthContext } from "../../../../../auth-context";
import { Prompt, useParams } from "react-router-dom";
import AssignClientsModal from "./assign-clients-modal";
import { withRouter } from "react-router-dom";
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1100px;
  width: 100%;
`;

const FormHeader = styled.header`
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #e1e2e6;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 20;

  @media (max-width: 900px) {
    align-items: stretch;
    flex-wrap: wrap;
    gap: 8px;
    // top: 55px;
  }
`;

const FormNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
`;

const FormNameLine = styled.div`
  align-items: center;
  display: flex;
  min-width: 0;
`;

const FormNameHint = styled.span`
  color: #555;
  font-size: 0.75rem;
  margin-top: 2px;
`;

const FormNameButton = styled.button`
  background: none;
  border: none;
  color: #333;
  cursor: pointer;
  font-family: ${(props) => props.theme.font.primary};
  font-size: 1.25rem;
  font-weight: 600;
  overflow: hidden;
  padding: 4px;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.color.secondary};
    outline-offset: 2px;
  }
`;

const EditNameButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: #777;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  padding: 6px;

  svg {
    font-size: 1.1rem;
  }

  &:hover {
    color: ${(props) => props.theme.color.secondary};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.color.secondary};
    outline-offset: 2px;
  }
`;

const FormNameInput = styled.input`
  background: ${(props) => props.theme.input.background};
  border: 1px solid ${(props) => props.theme.color.secondary};
  border-radius: ${(props) => props.theme.input.borderRadius};
  color: #333;
  font-family: ${(props) => props.theme.font.primary};
  font-size: 1.25rem;
  font-weight: 600;
  max-width: 500px;
  padding: 6px 8px;
  width: 100%;

  &:focus {
    box-shadow: 0 0 0 3px
      ${(props) => `${props.theme.color.secondary}45`};
    outline: none;
  }
`;

const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 900px) {
    flex: 1 0 100%;
    flex-wrap: wrap;
    gap: 4px 10px;
    width: 100%;
  }
`;

const TextAction = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.color.secondary};
  cursor: pointer;
  font-family: ${(props) => props.theme.font.primary};
  font-size: 0.9rem;
  font-weight: 600;
  padding: 8px 4px;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.color.secondary};
    outline-offset: 2px;
  }
`;

const SaveButton = styled(Button)`
  display: inline-flex;
  flex-shrink: 0;
  height: 40px;
  margin: 0;
  width: auto;
`;

const Container = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 0px 8px 0px #e1e2e6;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  margin: 12px 0px;
`;

const TextInput = styled.input`
  background: none;
  border: none;
  font-size: ${({ size }) => (size === "large" ? "1.5rem" : "1rem")};
  font-weight: 600;
  padding: 4px;
  max-width: 600px;
  width: 100%;
`;

const TextArea = styled.textarea`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;
  /* max-width: 600px; */
  width: 100%;
  font-weight: 500;
  height: 200px;
`;

const AddQuestion = styled.button`
  background: ${(props) =>
    props.warning ? "#f44336" : props.theme.button.background};
  border: none;
  border-radius: ${(props) => props.theme.button.borderRadius};
  color: ${(props) => props.theme.button.color};
  cursor: pointer;
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  height: 44px;
  margin: 16px 0 0 0;
  padding: 12px 18px;
  position: relative;
  transition: 0.2s ease all;
  opacity: ${(props) => (props.disabled ? "0.6" : "1")};

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 900px) {
    font-size: 0.875rem;
    height: 40px;
  }
`;

const newForm = {
  id: null,
  name: "",
  published: 0,
  survey_data: {
    title: "",
    description: "",
    questions: [],
  },
};

const UNSAVED_CHANGES_MESSAGE =
  "Your changes have not been saved. Are you sure you want to leave?";

const cloneForm = (form) => JSON.parse(JSON.stringify(form));

const Form = ({ forms, create, history, fetchSurveys }) => {
  const { user } = useContext(AuthContext);
  const { openSnackbar } = useContext(DashboardContext);
  const { id } = useParams();
  const loadFormID = Number(id);
  const [form, setForm] = useState("");
  const [initialFormSnapshot, setInitialFormSnapshot] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [showAssignedClients, setShowAssignedClients] = useState(false);
  const nameBeforeEdit = useRef("");
  const allowNavigation = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (loadFormID) {
      const getForm = forms.find((f) => f.id === loadFormID);
      if (getForm) {
        const loadedForm = cloneForm(getForm);
        setForm(loadedForm);
        setInitialFormSnapshot(JSON.stringify(loadedForm));
        setEditingName(false);
        allowNavigation.current = false;
      }
    }
  }, [loadFormID, forms]);

  useEffect(() => {
    if (create) {
      const emptyForm = cloneForm(newForm);
      setForm(emptyForm);
      setInitialFormSnapshot(JSON.stringify(emptyForm));
      setEditingName(false);
      allowNavigation.current = false;
    }
  }, [create]);

  const isDirty = Boolean(
    form &&
      initialFormSnapshot &&
      JSON.stringify(form) !== initialFormSnapshot
  );

  useEffect(() => {
    if (!isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const updateFormData = (data) => {
    setForm({ ...form, survey_data: { ...form.survey_data, ...data } });
  };

  const handleAddQuestion = (question) => {
    const newForm = { ...form };
    newForm.survey_data.questions.push(question);
    setForm(newForm);
    setSelectedQuestion(question.id);
  };

  const saveForm = () => {
    if (!validateForm()) {
      return;
    }

    if (create) {
      createPortalSurvey(user.id, form)
        .then(() => {
          allowNavigation.current = true;
          fetchSurveys();
          history.push(`/dashboard/forms/view`);
          openSnackbar("Check-in form created successfully.", false);
        })
        .catch((err) => {
          openSnackbar(
            "There was a problem updating your check-in form.",
            true
          );
        });
    } else {
      updatePortalSurvey(user.id, form.id, form)
        .then(() => {
          allowNavigation.current = true;
          fetchSurveys();
          history.push(`/dashboard/forms/view`);
          openSnackbar("Check-in form updated successfully.", false);
        })
        .catch((err) => {
          openSnackbar(
            "There was a problem updating your check-in form.",
            true
          );
        });
    }
  };

  const validateForm = () => {
    const questions = form.survey_data.questions || [];

    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      if (
        (question.responseType === "multiple_choice" ||
          question.responseType === "checkbox") &&
        (!question.options || question.options.length === 0)
      ) {
        openSnackbar(
          `Question ${i + 1} (${
            question.responseType === "multiple_choice"
              ? "Multiple Choice"
              : "Checkbox"
          }) must have at least one option.`,
          true
        );
        return false;
      }
    }

    return true;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = form.survey_data.questions.findIndex(
        (q) => q.id === active.id
      );
      const newIndex = form.survey_data.questions.findIndex(
        (q) => q.id === over.id
      );

      const newForm = { ...form };
      newForm.survey_data.questions = arrayMove(
        form.survey_data.questions,
        oldIndex,
        newIndex
      );
      setForm(newForm);
    }
  };

  const startEditingName = () => {
    nameBeforeEdit.current = form.name || "";
    setEditingName(true);
  };

  const handleNameKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setEditingName(false);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setForm({ ...form, name: nameBeforeEdit.current });
      setEditingName(false);
    }
  };

  if (!form) return null;
  return (
    <FormContainer>
      <Prompt
        when={isDirty}
        message={() =>
          allowNavigation.current ? true : UNSAVED_CHANGES_MESSAGE
        }
      />
      <FormHeader>
        <FormNameContainer>
          <FormNameLine>
            {editingName ? (
              <FormNameInput
                aria-label="Form name"
                autoFocus
                onBlur={() => setEditingName(false)}
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
                onKeyDown={handleNameKeyDown}
                placeholder="Untitled form"
                value={form.name || ""}
              />
            ) : (
              <>
                <FormNameButton type="button" onClick={startEditingName}>
                  {form.name || "Untitled form"}
                </FormNameButton>
                <EditNameButton
                  aria-label="Edit form name"
                  type="button"
                  onClick={startEditingName}
                >
                  <EditIcon />
                </EditNameButton>
              </>
            )}
          </FormNameLine>
          <FormNameHint>
            Only displayed on the portal. Clients will not see this.
          </FormNameHint>
        </FormNameContainer>
        <HeaderActions>
          {!create && (
            <>
              <TextAction
                type="button"
                onClick={() => setShowAssignedClients(true)}
              >
                Manage Assignments
              </TextAction>
              <AssignClientsModal
                open={showAssignedClients}
                onClose={() => setShowAssignedClients(false)}
                surveyID={form.id}
              />
            </>
          )}
          <SaveButton label="Save changes" onClick={saveForm} />
        </HeaderActions>
      </FormHeader>
      <Container>
        <TextInput
          error={false}
          placeholder="Untitled Form"
          value={form.survey_data.title}
          onChange={(e) => updateFormData({ title: e.target.value })}
          size="large"
        />
        <TextArea
          error={false}
          placeholder="Enter a description (optional)"
          value={form.survey_data.description}
          onChange={(e) => updateFormData({ description: e.target.value })}
        />
      </Container>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={form?.survey_data?.questions?.map((q) => q.id)}
          strategy={verticalListSortingStrategy}
        >
          {form?.survey_data?.questions?.map((question) => (
            <Question
              key={question.id}
              question={question}
              selected={selectedQuestion === question.id}
              setSelected={setSelectedQuestion}
              form={form}
              setForm={setForm}
            />
          ))}
        </SortableContext>
      </DndContext>
      <AddQuestion
        label="Add Question"
        onClick={() =>
          handleAddQuestion({
            id: uuidv4(),
            title: "",
            description: "",
            responseType: "multiple_choice",
            required: false,
            options: [],
          })
        }
      >
        Add Question
      </AddQuestion>
    </FormContainer>
  );
};

export default withRouter(Form);
